import { useState } from 'react';

/**
 * Custom hook to track failed image URLs and prevent retry loops
 * Prevents the browser from repeatedly requesting failed images
 */
export function useFailedImages() {
    const [failedImageUrls, setFailedImageUrls] = useState(new Set());

    /**
     * Create an onError handler for image elements
     * @param {string} fallbackUrl - URL to try as fallback (optional)
     * @returns {function} onError handler
     */
    const createImageErrorHandler = (fallbackUrl = null) => {
        return (e) => {
            const currentSrc = e.target.src;
            
            // If this URL already failed, don't retry
            if (failedImageUrls.has(currentSrc)) {
                if (fallbackUrl && !failedImageUrls.has(fallbackUrl)) {
                    e.target.src = fallbackUrl;
                } else {
                    // Both failed, hide the image
                    e.target.style.display = 'none';
                }
                return;
            }
            
            // Mark this URL as failed
            setFailedImageUrls(prev => new Set([...prev, currentSrc]));
            
            // Try fallback if provided
            if (fallbackUrl && !failedImageUrls.has(fallbackUrl)) {
                e.target.src = fallbackUrl;
            } else {
                // No fallback or fallback already failed
                e.target.style.display = 'none';
            }
        };
    };

    /**
     * Create an onError handler for background images (div elements)
     * @param {string} fallbackUrl - URL to try as fallback (optional)
     * @returns {function} onError handler
     */
    const createBackgroundImageErrorHandler = (fallbackUrl = null) => {
        return (e) => {
            const element = e.target;
            const currentStyle = element.style.backgroundImage;
            
            // Extract URL from backgroundImage style
            const urlMatch = currentStyle.match(/url\(['"]?([^'"]+)['"]?\)/);
            if (!urlMatch) return;
            
            const currentUrl = urlMatch[1];
            
            // If this URL already failed, don't retry
            if (failedImageUrls.has(currentUrl)) {
                if (fallbackUrl && !failedImageUrls.has(fallbackUrl)) {
                    element.style.backgroundImage = `url(${fallbackUrl})`;
                } else {
                    // Both failed, hide the element
                    element.style.display = 'none';
                }
                return;
            }
            
            // Mark this URL as failed
            setFailedImageUrls(prev => new Set([...prev, currentUrl]));
            
            // Try fallback if provided
            if (fallbackUrl && !failedImageUrls.has(fallbackUrl)) {
                element.style.backgroundImage = `url(${fallbackUrl})`;
            } else {
                // No fallback or fallback already failed
                element.style.display = 'none';
            }
        };
    };

    return {
        failedImageUrls,
        createImageErrorHandler,
        createBackgroundImageErrorHandler,
        setFailedImageUrls
    };
}
